import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { categories } from "@/mocks/categories";

export default function Navbar() {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const iconClass = isDropdownOpen ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line";

  return (
    <nav
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        scrolled
          ? "bg-background-50/98 backdrop-blur-md border-b border-background-200/80"
          : "bg-foreground-900/35 backdrop-blur-sm"
      }`}
    >
      <div className="flex items-center justify-between px-5 md:px-10 py-3.5">
        {/* Logo / Home */}
        <button
          onClick={() => {
            navigate("/");
            setIsMobileMenuOpen(false);
          }}
          className="text-lg md:text-xl font-heading font-bold text-background-50 hover:text-accent-300 transition-colors cursor-pointer whitespace-nowrap tracking-tight"
        >
          superbuy.sbs
        </button>

        {/* Desktop nav items */}
        <div className="hidden md:flex items-center gap-3">
          {/* Home button */}
          <button
            onClick={() => navigate("/")}
            className="px-4 py-2 text-sm font-medium text-background-50/90 hover:text-background-50 transition-colors cursor-pointer whitespace-nowrap rounded-full hover:bg-background-50/10"
          >
            Home
          </button>

          {/* Shopping Guide */}
          <button
            onClick={() => {
              navigate("/superbuy-shopping-guide");
              window.scrollTo({ top: 0, behavior: "smooth" });
            }}
            className="px-4 py-2 text-sm font-medium text-background-50/90 hover:text-background-50 transition-colors cursor-pointer whitespace-nowrap rounded-full hover:bg-background-50/10"
          >
            Shopping Guide
          </button>

          {/* Categories dropdown */}
          <div ref={dropdownRef} className="relative">
            <button
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap bg-background-50/15 backdrop-blur-sm text-background-50 border border-background-50/20 hover:bg-background-50/25"
            >
              Categories
              <i className={`${iconClass} transition-transform duration-200 text-base`}></i>
            </button>

            {isDropdownOpen && (
              <div className="absolute top-full mt-2 right-0 w-64 bg-background-50 rounded-xl border border-background-200/70 shadow-lg py-2 max-h-80 overflow-y-auto">
                {categories.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="block px-4 py-2.5 text-sm text-foreground-800 hover:bg-accent-50 hover:text-accent-800 transition-colors cursor-pointer"
                    onClick={() => setIsDropdownOpen(false)}
                  >
                    {cat.name}
                  </a>
                ))}
              </div>
            )}
          </div>

          {/* CTA */}
          <a
            href="https://kaspreadsheet.com/"
            target="_blank"
            rel="noopener nofollow"
            className="inline-flex items-center px-5 py-2 rounded-full text-sm font-semibold transition-all duration-200 cursor-pointer whitespace-nowrap bg-background-50 text-primary-600 hover:bg-background-100"
          >
            superbuy spreadsheet
            <i className="ri-arrow-right-up-line ml-2 text-base"></i>
          </a>
        </div>

        {/* Mobile hamburger */}
        <button
          className="md:hidden w-10 h-10 flex items-center justify-center rounded-lg bg-background-50/10 text-background-50"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        >
          <i className={`text-xl ${isMobileMenuOpen ? "ri-close-line" : "ri-menu-line"}`}></i>
        </button>
      </div>

      {/* Mobile menu panel */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-foreground-900/98 backdrop-blur-md border-t border-foreground-700/30 px-5 py-4">
          <div className="space-y-1 mb-4">
            <button
              onClick={() => {
                navigate("/");
                setIsMobileMenuOpen(false);
              }}
              className="flex items-center gap-3 w-full px-3 py-3 text-sm font-medium text-background-50 hover:bg-background-50/10 rounded-lg transition-colors cursor-pointer"
            >
              <i className="ri-home-4-line text-lg text-accent-400"></i>
              Home
            </button>
            <button
              onClick={() => {
                navigate("/superbuy-shopping-guide");
                setIsMobileMenuOpen(false);
                window.scrollTo({ top: 0, behavior: "smooth" });
              }}
              className="flex items-center gap-3 w-full px-3 py-3 text-sm font-medium text-background-50 hover:bg-background-50/10 rounded-lg transition-colors cursor-pointer"
            >
              <i className="ri-book-open-line text-lg text-accent-400"></i>
              Shopping Guide
            </button>
          </div>

          <p className="text-xs font-semibold text-background-50/50 uppercase tracking-wider px-3 mb-2 mt-5">Categories</p>
          <div className="grid grid-cols-2 gap-1">
            {categories.map((cat) => (
              <a
                key={cat.name}
                href={cat.url}
                target="_blank"
                rel="noopener nofollow"
                className="block px-3 py-2.5 text-sm text-background-50/80 hover:text-background-50 hover:bg-background-50/10 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {cat.name}
              </a>
            ))}
          </div>

          <a
            href="https://kaspreadsheet.com/"
            target="_blank"
            rel="noopener nofollow"
            className="flex items-center justify-center gap-2 w-full mt-4 px-5 py-3 bg-background-50 text-primary-600 font-semibold text-sm rounded-full hover:bg-background-100 transition-all duration-200 cursor-pointer whitespace-nowrap"
          >
            superbuy spreadsheet
            <i className="ri-arrow-right-up-line text-base"></i>
          </a>
        </div>
      )}
    </nav>
  );
}